import React from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { Form, Input } from 'antd';
import styled from 'styled-components';

import { FILE_STATUS } from 'common/constants.files';
import BrochureAndDocsThumbnails from 'components/CommunityInfo/general/documents/BrochureAndDocsThumbnails';
import Checkbox from 'components/lib/Checkbox';
import LoaderRibbed from 'components/lib/LoaderRibbed';

import { RemoveIconContainer, ThumbnailContainer, ThumbnailLoader } from './ShopDataModalThumbnail';

const Row = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
  width: 100%;
  padding: 20px 0;
  border-bottom: 1px solid var(--line-gray);
`;

const FileNameOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 22px 6px 5px;
  font-size: 12px;
  color: var(--white);
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.14);
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.65));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  z-index: 2;
`;

const Details = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
  min-width: 0;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const FieldLabel = styled.div`
  flex-shrink: 0;
  width: 74px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
  white-space: nowrap;
`;

const FieldContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CategoryField = styled(Form.Item)`
  margin: 0;

  .ant-form-item-control-input {
    min-height: 0 !important;
    align-items: flex-start;
  }

  .ant-form-item-explain {
    padding-top: 10px;
  }
`;

const TagGroup = styled(Checkbox.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  padding-top: 2px;
`;

const TagLabel = styled.span`
  font-size: 13px;
  color: var(--text-primary);
`;

const DescriptionField = styled(Form.Item)`
  margin: 0;
`;

const DescriptionTextArea = styled(Input.TextArea)`
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-primary);
  border-radius: 0;
  background-color: var(--background-gray-light);
`;

const AnalysisError = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--danger);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.75);
  z-index: 10;

  span {
    font-size: 12px;
    color: var(--text-secondary);
  }
`;

const CloseIcon = styled(CloseOutlined)`
  color: var(--white);
  font-size: 12px;
`;

const ShopDataModalDocumentsUploadThumbnail = (
  originNode: any,
  file: any,
  fileList: any,
  actions: any,
  categories: { label: string; value: number }[],
  disabled?: boolean
) => {
  if (file?.status === FILE_STATUS.REMOVED) {
    return null;
  }

  const fileUploading = file.status !== FILE_STATUS.DONE;
  const fileIndex = fileList.indexOf(file);
  const previewUrl = file.mock ? file.url : (file.previewUrl ?? file.url ?? file.thumbUrl ?? '');
  const updatedFile = { ...file.originFileObj, file_url: previewUrl, file_name: file.name };
  // Fields are locked while uploading or analyzing; once analysis finishes the user can edit both freely.
  const fieldsDisabled = disabled || fileUploading || file.analysis_pending;

  return (
    <Row>
      <ThumbnailContainer>
        {fileUploading && <ThumbnailLoader />}
        {!fileUploading && (
          <>
            <BrochureAndDocsThumbnails file={updatedFile} />
            <RemoveIconContainer
              onClick={() => {
                if (!disabled) actions.remove();
              }}
            >
              <CloseIcon />
            </RemoveIconContainer>
            <FileNameOverlay title={file.name}>{file.name}</FileNameOverlay>
          </>
        )}
      </ThumbnailContainer>
      <Details>
        <FieldRow>
          <FieldLabel>Tags</FieldLabel>
          <FieldContent>
            <CategoryField
              name={['documents', fileIndex, 'file_category_ids']}
              preserve={false}
              rules={[{ required: true, message: 'Please select at least one tag' }]}
            >
              <TagGroup className="green" disabled={fieldsDisabled}>
                {categories.map((category) => (
                  <Checkbox key={category.value} className="green" value={category.value}>
                    <TagLabel>{category.label}</TagLabel>
                  </Checkbox>
                ))}
              </TagGroup>
            </CategoryField>
            {file.analysis_error_message && !file.file_category_ids?.length && (
              <AnalysisError title={file.analysis_error_message}>Analysis failed. Select tags manually.</AnalysisError>
            )}
          </FieldContent>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Description</FieldLabel>
          <FieldContent>
            <DescriptionField name={['documents', fileIndex, 'summary']} preserve={false}>
              <DescriptionTextArea placeholder="Add a description" autoSize={{ minRows: 3 }} disabled={fieldsDisabled} />
            </DescriptionField>
          </FieldContent>
        </FieldRow>
        {file.analysis_pending && (
          <LoadingOverlay title="AI is analyzing this document — fields unlock in a moment">
            <LoaderRibbed size="small" />
            <span>Analyzing document…</span>
          </LoadingOverlay>
        )}
      </Details>
    </Row>
  );
};

export default ShopDataModalDocumentsUploadThumbnail;
